/**
 * src/platform/fileCapture.web.ts — WEB file acquisition shim (W2 web-companion parity).
 *
 * Web counterpart to fileCapture.ts. Uses a hidden <input type=file> + the File API to read
 * bytes as base64. Imports NO expo-* native modules (keeps them out of the web bundle).
 *
 *   - pickDocument()  → single PDF/image via file dialog
 *   - pickDocuments() → multiple files (historical import)
 *   - capturePhoto()  → image input with capture="environment" (opens the camera on mobile
 *                       browsers, the file dialog on desktop) — the spec's web camera shim
 *   - fileToAcquired(file) → convert a drag-dropped File to AcquiredFile (drag-drop capture)
 *   - readUriAsBase64() → not used on web (throws)
 *   - SUPPORTS_NATIVE_CAMERA = false
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
export type AcquiredFile = { filename: string; mimeType: string; contentBase64: string };

export const SUPPORTS_NATIVE_CAMERA = false;

/** Encode an ArrayBuffer as base64 without blowing the call stack on large files. */
async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  // btoa is available in all browsers; operates on a binary string.
  return btoa(binary);
}

/** Convert a File (e.g. from a drag-drop event) into an AcquiredFile. */
export async function fileToAcquired(file: File): Promise<AcquiredFile> {
  return { filename: file.name, mimeType: file.type || 'application/octet-stream', contentBase64: await fileToBase64(file) };
}

type DialogOpts = { accept: string; multiple?: boolean; capture?: 'environment' | 'user' };

function openFileDialog(opts: DialogOpts): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = opts.accept;
    if (opts.multiple) input.multiple = true;
    if (opts.capture) input.setAttribute('capture', opts.capture);
    input.style.position = 'fixed';
    input.style.left = '-10000px';

    let settled = false;
    const cleanup = () => {
      window.removeEventListener('focus', onFocus);
      input.remove();
    };
    const finish = (files: File[]) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(files);
    };
    input.onchange = () => finish(input.files ? Array.from(input.files) : []);
    // Browsers fire no event when the dialog is canceled. When the window regains focus
    // and no file was chosen shortly after, treat it as a cancel.
    const onFocus = () => { setTimeout(() => finish([]), 600); };
    window.addEventListener('focus', onFocus);

    document.body.appendChild(input);
    input.click();
  });
}

export async function pickDocument(): Promise<AcquiredFile | null> {
  const files = await openFileDialog({ accept: 'image/*,application/pdf' });
  const file = files[0];
  return file ? fileToAcquired(file) : null;
}

export async function pickDocuments(): Promise<AcquiredFile[]> {
  const files = await openFileDialog({ accept: '*/*', multiple: true });
  return Promise.all(files.map(fileToAcquired));
}

export async function capturePhoto(): Promise<AcquiredFile | null> {
  const files = await openFileDialog({ accept: 'image/*', capture: 'environment' });
  const file = files[0];
  return file ? fileToAcquired(file) : null;
}

export async function readUriAsBase64(): Promise<string> {
  throw new Error('readUriAsBase64() is not used on web.');
}
